import { useState, useEffect, useRef, useCallback, forwardRef, useImperativeHandle } from 'react';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { cn } from '@/lib/utils';
import { getStaffForMentions, StaffMember } from '@/services/data/chat';

// ============================================
// Global cache for staff data
// This prevents repeated API calls when multiple MentionInput instances mount
// ============================================
let staffCache: StaffMember[] | null = null;
let staffCachePromise: Promise<StaffMember[]> | null = null;

async function getCachedStaff(): Promise<StaffMember[]> {
  // Return from cache if available
  if (staffCache) return staffCache;

  // Return existing promise if a fetch is in progress (deduplication)
  if (staffCachePromise !== null) return staffCachePromise;
  
  // Start new fetch and cache the promise
  staffCachePromise = getStaffForMentions().then(({ data }) => {
    staffCache = data || [];
    staffCachePromise = null; // Clear promise after completion
    return staffCache;
  }).catch((error) => {
    staffCachePromise = null; // Clear on error to allow retry
    console.error('Failed to fetch staff for mentions:', error);
    return [];
  });
  
  return staffCachePromise;
}

// Role to display title mapping for client-facing chat
const CHAT_ROLE_TITLES: Record<string, string> = {
  'personal_brand_manager': 'Personal Brand Manager',
  'post_prod_manager': 'Responsable de production',
  'ops_assistant': 'Responsable de production',
  'video_editor': 'Virality Expert',
  'monteur': 'Monteur',
  'ops': 'Chargé de Production',
  'admin': 'Admin',
  'super_admin': 'Admin',
  'cm': 'Community Manager',
  'csm': 'CSM',
};

export function getDisplayTitle(staff: StaffMember): string | null {
  if (staff.job_title) return staff.job_title;
  for (const role of staff.roles) {
    if (CHAT_ROLE_TITLES[role]) return CHAT_ROLE_TITLES[role];
  }
  return null;
}

export interface MentionData {
  id: string;
  name: string;
}

export interface MentionInputRef {
  focus: () => void;
  clear: () => void;
}

interface MentionInputProps {
  value: string;
  onChange: (value: string, mentions: MentionData[]) => void;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  onSubmit?: () => void;
  /** Override the default staff list (e.g. with conversation participants only) */
  staffOverride?: StaffMember[];
  /** Customer to show at top of suggestions (for admins) */
  customer?: {
    id: string;
    first_name: string | null;
    last_name: string | null;
    email: string;
    avatar_url: string | null;
  } | null;
}

export const MentionInput = forwardRef<MentionInputRef, MentionInputProps>(
  ({ value, onChange, placeholder, disabled, className, onSubmit, staffOverride, customer }, ref) => {
    const [showSuggestions, setShowSuggestions] = useState(false);
    const [suggestions, setSuggestions] = useState<StaffMember[]>([]);
    const [filteredSuggestions, setFilteredSuggestions] = useState<StaffMember[]>([]);
    const [selectedIndex, setSelectedIndex] = useState(0);
    const [mentionQuery, setMentionQuery] = useState('');
    const [mentionStartIndex, setMentionStartIndex] = useState(-1);
    const [mentions, setMentions] = useState<MentionData[]>([]);
    const inputRef = useRef<HTMLTextAreaElement>(null);

    useImperativeHandle(ref, () => ({
      focus: () => inputRef.current?.focus(),
      clear: () => {
        onChange('', []);
        setMentions([]);
      },
    }));

    // Auto-resize textarea when value changes (e.g. after clearing)
    useEffect(() => {
      if (inputRef.current) {
        inputRef.current.style.height = 'auto';
        inputRef.current.style.height = `${Math.min(inputRef.current.scrollHeight, 80)}px`;
      }
    }, [value]);

    // Load staff members: use staffOverride if provided, otherwise global cache
    useEffect(() => {
      if (staffOverride) {
        setSuggestions(staffOverride);
      } else {
        getCachedStaff().then(setSuggestions);
      }
    }, [staffOverride]);

    // Filter suggestions based on query
    useEffect(() => {
      // Build full list with customer at top if provided
      let allSuggestions: StaffMember[] = [];
      
      if (customer) {
        const customerAsMember: StaffMember = {
          id: customer.id,
          first_name: customer.first_name,
          last_name: customer.last_name,
          email: customer.email,
          avatar_url: customer.avatar_url,
          job_title: 'Client',
          roles: ['customer'],
        };
        allSuggestions = [customerAsMember, ...suggestions];
      } else {
        allSuggestions = suggestions;
      }
      
      if (!mentionQuery) {
        setFilteredSuggestions(allSuggestions);
        return;
      }

      const query = mentionQuery.toLowerCase();
      const filtered = allSuggestions.filter((s) => {
        const fullName = `${s.first_name || ''} ${s.last_name || ''}`.toLowerCase();
        const email = s.email.toLowerCase();
        return fullName.includes(query) || email.includes(query);
      });

      setFilteredSuggestions(filtered);
      setSelectedIndex(0);
    }, [mentionQuery, suggestions, customer]);

    const getCaretPosition = () => inputRef.current?.selectionStart || 0;

    const handleInputChange = useCallback(
      (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        const newValue = e.target.value;
        const caretPos = e.target.selectionStart || 0;

        // Check if we're typing after @
        const textBeforeCaret = newValue.slice(0, caretPos);
        const atIndex = textBeforeCaret.lastIndexOf('@');

        if (atIndex !== -1) {
          // Check if there's a space before @ or it's at the start
          const charBeforeAt = atIndex > 0 ? textBeforeCaret[atIndex - 1] : ' ';
          if (charBeforeAt === ' ' || atIndex === 0) {
            const query = textBeforeCaret.slice(atIndex + 1);
            // Only show suggestions if no space after @
            if (!query.includes(' ')) {
              setMentionQuery(query);
              setMentionStartIndex(atIndex);
              setShowSuggestions(true);
              onChange(newValue, mentions);
              return;
            }
          }
        }

        setShowSuggestions(false);
        setMentionStartIndex(-1);
        onChange(newValue, mentions);
      },
      [onChange, mentions]
    );

    const insertMention = useCallback(
      (staff: StaffMember) => {
        if (mentionStartIndex === -1) return;

        const name = `${staff.first_name || ''} ${staff.last_name || ''}`.trim() || staff.email;
        const beforeMention = value.slice(0, mentionStartIndex);
        const afterCaret = value.slice(getCaretPosition());
        const newValue = `${beforeMention}@${name} ${afterCaret}`;

        const newMention: MentionData = { id: staff.id, name };
        const updatedMentions = [...mentions, newMention];

        setMentions(updatedMentions);
        onChange(newValue, updatedMentions);
        setShowSuggestions(false);
        setMentionStartIndex(-1);

        // Focus and move caret after mention
        setTimeout(() => {
          if (inputRef.current) {
            const newCaretPos = beforeMention.length + name.length + 2;
            inputRef.current.focus();
            inputRef.current.setSelectionRange(newCaretPos, newCaretPos);
          }
        }, 0);
      },
      [mentionStartIndex, value, mentions, onChange]
    );

    const handleKeyDown = useCallback(
      (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
        if (!showSuggestions) {
          // On desktop: Enter alone = send, Shift/Alt+Enter = newline
          // On mobile: Enter always = newline (user taps the send button)
          const isMobile = 'ontouchstart' in globalThis || navigator.maxTouchPoints > 0;
          if (e.key === 'Enter' && !isMobile && !e.shiftKey && !e.altKey) {
            e.preventDefault();
            onSubmit?.();
          }
          return;
        }

        switch (e.key) {
          case 'ArrowDown':
            e.preventDefault();
            setSelectedIndex((prev) =>
              prev < filteredSuggestions.length - 1 ? prev + 1 : prev
            );
            break;
          case 'ArrowUp':
            e.preventDefault();
            setSelectedIndex((prev) => (prev > 0 ? prev - 1 : 0));
            break;
          case 'Enter':
          case 'Tab':
            e.preventDefault();
            if (filteredSuggestions[selectedIndex]) {
              insertMention(filteredSuggestions[selectedIndex]);
            }
            break;
          case 'Escape':
            setShowSuggestions(false);
            break;
        }
      },
      [showSuggestions, filteredSuggestions, selectedIndex, insertMention, onSubmit]
    );

    const getInitials = (firstName?: string | null, lastName?: string | null) => {
      return `${firstName?.[0] || ''}${lastName?.[0] || ''}`.toUpperCase() || '?';
    };

    // Generate a consistent gradient based on user's name (same as CustomerChatHeader)
    const getGradientFromName = (firstName?: string | null, lastName?: string | null): string => {
      const name = `${firstName || ''}${lastName || ''}`;
      let hash = 0;
      for (let i = 0; i < name.length; i++) {
        hash = (name.codePointAt(i) || 0) + ((hash << 5) - hash);
      }
      
      const gradients = [
        'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
        'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
        'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)',
        'linear-gradient(135deg, #fa709a 0%, #fee140 100%)',
        'linear-gradient(135deg, #a8edea 0%, #fed6e3 100%)',
        'linear-gradient(135deg, #ff9a9e 0%, #fecfef 100%)',
        'linear-gradient(135deg, #ffecd2 0%, #fcb69f 100%)',
        'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        'linear-gradient(135deg, #6a11cb 0%, #2575fc 100%)',
      ];
      
      return gradients[Math.abs(hash) % gradients.length];
    };

    return (
      <div className="relative flex-1">
        <textarea
          ref={inputRef}
          value={value}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          disabled={disabled}
          rows={1}
          className={cn(
            'flex min-h-[40px] max-h-[80px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 resize-none overflow-y-auto leading-5',
            className
          )}
          onInput={(e) => {
            const target = e.target as HTMLTextAreaElement;
            target.style.height = 'auto';
            target.style.height = `${Math.min(target.scrollHeight, 80)}px`;
          }}
        />

        {/* Suggestions dropdown */}
        {showSuggestions && filteredSuggestions.length > 0 && (
          <div className="absolute bottom-full left-0 right-0 mb-1 max-h-48 overflow-auto rounded-md border bg-popover shadow-md z-50">
            {filteredSuggestions.map((staff, index) => (
              <button
                key={staff.id}
                type="button"
                onClick={() => insertMention(staff)}
                className={cn(
                  'flex w-full items-center gap-2 px-3 py-2 text-left text-sm transition-colors',
                  index === selectedIndex ? 'bg-accent' : 'hover:bg-muted'
                )}
              >
                <Avatar className="h-6 w-6">
                  <AvatarImage src={staff.avatar_url || undefined} />
                  <AvatarFallback 
                    className="text-xs text-white font-medium"
                    style={{ background: getGradientFromName(staff.first_name, staff.last_name) }}
                  >
                    {getInitials(staff.first_name, staff.last_name)}
                  </AvatarFallback>
                </Avatar>
                <div className="flex-1 overflow-hidden">
                  <p className="font-medium truncate">
                    {staff.first_name} {staff.last_name}
                    {getDisplayTitle(staff) && (
                      <span className="ml-1 text-muted-foreground font-normal">· {getDisplayTitle(staff)}</span>
                    )}
                  </p>
                </div>
              </button>
            ))}
          </div>
        )}

        {showSuggestions && filteredSuggestions.length === 0 && mentionQuery && (
          <div className="absolute bottom-full left-0 right-0 mb-1 rounded-md border bg-popover p-3 shadow-md z-50">
            <p className="text-sm text-muted-foreground">Aucun membre trouvé</p>
          </div>
        )}
      </div>
    );
  }
);

MentionInput.displayName = 'MentionInput';
